import { useEffect, useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertCircle } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@quebon/ui/components/dialog'
import { memberApi, ResetLearningProgressResult } from '@/api/b2b'

interface Props {
  target: { userId: number; fullName: string } | null
  onClose: () => void
  onSuccess?: () => void
}

/** 학습데이터 초기화 모달(비가역) — 이름 타이핑 가드. 동작은 StudentManagement 인라인 모달과 동일. */
export function ResetLearningProgressModal({ target, onClose, onSuccess }: Props) {
  const { t } = useTranslation(['admin', 'common'])
  const queryClient = useQueryClient()
  const [confirmName, setConfirmName] = useState('')
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (target) {
      setConfirmName('')
      setReason('')
    }
  }, [target])

  const mutation = useMutation({
    mutationFn: ({ userId, resetReason }: { userId: number; resetReason: string }) =>
      memberApi.resetLearningProgress(userId, resetReason),
    onSuccess: (response) => {
      const result: ResetLearningProgressResult | undefined = response.data?.data
      const counts = result?.deletedCounts || {}
      const summary = Object.entries(counts)
        .filter(([, v]) => typeof v === 'number' && v > 0)
        .map(([k, v]) => `${k === 'gameStates' ? t('admin:students.resetProgress.gameStatesLabel') : k}: ${v}`)
        .join('\n')
      const deletedSection = summary
        ? `\n\n${t('admin:students.toast.resetProgressDeleted')}\n${summary}`
        : ''
      alert(t('admin:students.toast.resetProgressSuccess', { message: result?.message ?? '', summary: deletedSection }))
      onClose()
      onSuccess?.()
      queryClient.invalidateQueries({ queryKey: ['members'] })
    },
    onError: (error: any) => {
      console.error('Learning progress reset failed:', error)
      let errorMessage = t('admin:students.toast.resetProgressFailed')
      if (error?.response?.data?.error) {
        const errorObj = error.response.data.error
        if (typeof errorObj === 'string') errorMessage = errorObj
        else if (errorObj?.message) errorMessage = errorObj.message
      }
      alert(t('admin:students.toast.errorPrefix', { message: errorMessage }))
    },
  })

  return (
    <Dialog open={!!target} onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-md p-0 gap-0 grid-rows-[auto_minmax(0,1fr)_auto]">
        <DialogHeader className="px-5 py-4 border-b pr-10">
          <DialogTitle>{t('admin:students.resetProgress.title')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 px-5 py-4 overflow-y-auto">
          <p className="text-sm text-gray-600">
            <Trans
              i18nKey="students.resetProgress.description"
              ns="admin"
              values={{ name: target?.fullName ?? '' }}
              components={{ strong: <strong /> }}
            />
          </p>
          <div className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-red-700 space-y-1">
              <p className="font-medium">{t('admin:students.resetProgress.warning1')}</p>
              <p>{t('admin:students.resetProgress.warning2')}</p>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:students.resetProgress.confirmLabel')}</label>
            <input
              type="text"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              placeholder={target?.fullName || ''}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t('admin:students.resetProgress.reasonLabel')}
            </label>
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={3}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:ring-2 focus:ring-red-500"
              placeholder={t('admin:students.resetProgress.reasonPlaceholder')}
            />
          </div>
        </div>
        <DialogFooter className="mx-0 mb-0 px-5">
          <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
            {t('common:button.cancel')}
          </button>
          <button
            type="button"
            onClick={() => {
              if (target) mutation.mutate({ userId: target.userId, resetReason: reason.trim() })
            }}
            disabled={
              mutation.isPending
              || confirmName.trim() !== (target?.fullName ?? '__never__')
              || reason.trim().length === 0
            }
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {mutation.isPending ? t('admin:students.resetProgress.submitting') : t('admin:students.resetProgress.submit')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
